import { useState } from "react";
import { Link } from "react-router-dom";
import { cn } from "@/lib/utils";

// The lister's headshot on a listing card (the realtor, or the owner for a
// private listing). Tapping it reveals the lister's full name and a link to all
// of their listings (/hosts/:id). It sits inline next to the Realtor/Verified
// badges. All pointer/click events are stopped so tapping never starts a swipe
// or opens the photo expand.
//
// `above`: put the popover ABOVE the avatar (used on the swipe card, where the
// avatar sits low on the card); default puts it below (hero card, avatar on top).
function firstNameOf(name) {
  return (name || "").trim().split(/\s+/)[0] || "this agent";
}

export default function ListerAvatar({ listing, above = false, size = "h-10 w-10", className = "" }) {
  const [open, setOpen] = useState(false);
  if (!listing || (!listing.lister_photo && !listing.lister_name)) return null;

  const name = listing.lister_name || "Lister";
  const hostId = listing.owner_id;
  const brokerage = listing.lister_brokerage || listing.courtesy_of;

  return (
    <div
      className={cn("pointer-events-auto relative", className)}
      onPointerDown={(e) => e.stopPropagation()}
    >
      <button
        type="button"
        aria-label={`About ${name}`}
        onPointerDown={(e) => e.stopPropagation()}
        onClick={(e) => {
          e.stopPropagation();
          setOpen((o) => !o);
        }}
        className={cn("block shrink-0 overflow-hidden rounded-full bg-slate-100 ring-2 ring-white/90 shadow", size)}
      >
        {listing.lister_photo ? (
          <img
            src={listing.lister_photo}
            alt={name}
            loading="lazy"
            decoding="async"
            className="h-full w-full object-cover"
          />
        ) : (
          <span className="flex h-full w-full items-center justify-center bg-brand-primary text-sm font-semibold text-white">
            {name.slice(0, 1).toUpperCase()}
          </span>
        )}
      </button>

      {open && (
        <div
          className={cn(
            "absolute left-0 z-30 w-56 rounded-2xl bg-night-surface p-3.5 text-left shadow-ambient ring-1 ring-night-line",
            above ? "bottom-12" : "top-12"
          )}
          onPointerDown={(e) => e.stopPropagation()}
          onClick={(e) => e.stopPropagation()}
        >
          <p className="text-sm font-bold text-night-text">{name}</p>
          {brokerage && <p className="mt-0.5 text-xs text-night-sub">{brokerage}</p>}
          {hostId && (
            <Link
              to={`/hosts/${hostId}`}
              onPointerDown={(e) => e.stopPropagation()}
              onClick={(e) => e.stopPropagation()}
              className="mt-2 inline-block text-sm font-semibold text-brand-lime hover:underline"
            >
              See more of {firstNameOf(name)}&apos;s listings →
            </Link>
          )}
        </div>
      )}
    </div>
  );
}
